import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { asArray, errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import usePosts from '../lib/usePosts';
import PostComposer from './PostComposer';
import PostCard from './PostCard';
import Stories from './Stories';
import StoryViewer from './StoryViewer';
import { PlusIcon, CloseIcon } from './icons';

/** Hem: stories högst upp, sedan flödet. */
export default function Feed({ onViewProfile }) {
  const { user } = useAuth();
  const [tag, setTag] = useState('');
  const url = tag ? `/posts/feed?tag=${encodeURIComponent(tag)}` : '/posts/feed';
  const { posts, loading, error, hasMore, loadMore, prepend, remove } = usePosts(url);

  const [groups, setGroups] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [composing, setComposing] = useState(false);
  const [storyError, setStoryError] = useState('');
  const storyFileRef = useRef(null);

  const loadStories = useCallback(() => {
    api.get('/stories')
      .then(res => setGroups(asArray(res.data)))
      .catch(() => { /* flödet fungerar utan stories */ });
  }, []);

  useEffect(() => { loadStories(); }, [loadStories]);

  const uploadStory = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setStoryError('');
    try {
      const data = new FormData();
      data.append('media', file);
      await api.post('/stories', data);
      loadStories();
    } catch (err) {
      setStoryError(errorMessage(err, 'Storyn kunde inte laddas upp.'));
      setTimeout(() => setStoryError(''), 4000);
    } finally {
      event.target.value = '';
    }
  };

  return (
    <div>
      {composing && (
        <PostComposer onPosted={prepend} onClose={() => setComposing(false)} />
      )}

      {viewing && (
        <StoryViewer
          group={viewing}
          onClose={() => { setViewing(null); loadStories(); }}
        />
      )}

      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
        ref={storyFileRef}
        onChange={uploadStory}
        hidden
      />

      <Stories
        groups={groups}
        me={user}
        onOpen={setViewing}
        onCreate={() => storyFileRef.current.click()}
      />

      {storyError && <p className="notice notice--error">{storyError}</p>}

      <button className="create-post" onClick={() => setComposing(true)}>
        <PlusIcon size={19} />
        Skapa inlägg
      </button>

      {tag && (
        <div className="feed-filter">
          <span>#{tag}</span>
          <button onClick={() => setTag('')} aria-label="Rensa filter">
            <CloseIcon size={15} />
          </button>
        </div>
      )}

      {error && <p className="notice notice--error">{error}</p>}
      {loading && <p className="empty">Hämtar inlägg…</p>}

      {!loading && posts.length === 0 && (
        <p className="empty">
          {tag
            ? `Inga inlägg med #${tag} än.`
            : 'Inga inlägg än. Bli först — lägg upp en bild på bygget!'}
        </p>
      )}

      {posts.map(post => (
        <PostCard
          key={post.id}
          post={post}
          onDeleted={remove}
          onViewProfile={onViewProfile}
          onTag={setTag}
        />
      ))}

      {hasMore && (
        <button className="btn btn--outline btn--block" onClick={loadMore}>
          Visa äldre inlägg
        </button>
      )}
    </div>
  );
}
